import { DeleteOutline, StickyNote2Outlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import { Badge, IconButton, TableCell, TableRow, TextField, Tooltip } from "@mui/material";

/**
 * One month of the received-commission ledger. A row is saved on its own — a draft becomes a
 * recorded month when it is confirmed, and an edited month keeps its confirm button until the
 * change actually reaches the server, so nothing typed here is left sitting unsaved.
 */
const CommissionMonthRow = ({
  row,
  oneTime = false,
  errors = {},
  disabled = false,
  saving = false,
  maxDate,
  onChange,
  onConfirm,
  onOpenNotes,
  onDelete,
}) => {
  const isDraft = !row.id;
  const notesCount = Number(row.notesCount || 0);

  return (
    <TableRow>
      {!oneTime && (
        <TableCell sx={{ verticalAlign: "top" }}>
          <TextField
            type="month"
            size="small"
            value={row.month || ""}
            disabled={disabled}
            error={Boolean(errors.month)}
            helperText={errors.month}
            onChange={(event) => onChange({ month: event.target.value })}
            inputProps={{ max: maxDate?.slice(0, 7) }}
          />
        </TableCell>
      )}
      <TableCell sx={{ verticalAlign: "top" }}>
        <TextField
          type="number"
          size="small"
          placeholder="0.00"
          value={row.amount ?? ""}
          disabled={disabled}
          error={Boolean(errors.amount)}
          helperText={errors.amount}
          onChange={(event) => onChange({ amount: event.target.value })}
          InputProps={{ startAdornment: <span className="pr-1 text-app-text-muted">$</span> }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <TextField
          type="date"
          size="small"
          value={row.receivedDate || ""}
          disabled={disabled}
          error={Boolean(errors.receivedDate)}
          helperText={errors.receivedDate}
          onChange={(event) => onChange({ receivedDate: event.target.value })}
          inputProps={{ max: maxDate }}
        />
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
        {(row.dirty || isDraft) && (
          <Tooltip
            title={isDraft ? (oneTime ? "Record commission" : "Record this month") : "Save changes"}
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={disabled || saving}
                onClick={onConfirm}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            isDraft
              ? oneTime
                ? "Record commission before adding notes"
                : "Record the month before adding notes"
              : oneTime
              ? "Notes for this commission"
              : "Notes for this month"
          }
        >
          <span>
            <IconButton size="small" disabled={isDraft} onClick={onOpenNotes}>
              <Badge badgeContent={notesCount} color="primary" overlap="circular">
                <StickyNote2Outlined fontSize="small" />
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            disabled
              ? "Commission is marked complete"
              : oneTime
              ? "Delete this commission"
              : "Delete this month"
          }
        >
          <span>
            <IconButton size="small" color="error" disabled={disabled} onClick={onDelete}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default CommissionMonthRow;
